import { FC, useMemo, useState } from 'react';
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { icon_search } from '@gn-audio/icons';
import {
  getContrastThemeClass,
  JabraIcon,
  resolveBackgroundStyle,
  useFallbackTranslation,
} from '@gn-audio/jss-components';

import { useSpotTracking } from 'utils/hooks/useSpotTracking';

import { DownloadableResourceGroup } from './DownloadableResourceGroup';
import { DownloadableResourceListProps } from './downloadableResourceListTypes';

const DownloadableResourceList: FC<DownloadableResourceListProps> = ({ fields }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { background, groups, showSearchBox, headline } = fields;
  const { t } = useFallbackTranslation();
  const spotRef = useSpotTracking(fields.trackingSpotName?.value);

  const spotThemeClass = getContrastThemeClass(background?.value as string);
  const spotBackgroundStyle = resolveBackgroundStyle(background?.value as string);
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groups;

    const searchTermLower = searchTerm.toLowerCase();

    return groups
      ?.map((group) => ({
        ...group,
        items: group.items?.filter((item) => {
          const localHeadline = item.headline?.value?.toLowerCase() || '';
          const localDescription = item.description?.value?.toLowerCase() || '';
          const globalHeadline = item.headlineGlobal?.value?.toLowerCase() || '';
          const globalDescription = item.descriptionGlobal?.value?.toLowerCase() || '';

          return (
            localHeadline.includes(searchTermLower) ||
            localDescription.includes(searchTermLower) ||
            globalHeadline.includes(searchTermLower) ||
            globalDescription.includes(searchTermLower)
          );
        }),
      }))
      .filter((group) => group.items?.length > 0);
  }, [groups, searchTerm]);

  const hasNoResults = searchTerm && (!filteredGroups || filteredGroups.length === 0);

  return (
    <div
      ref={spotRef}
      data-wt-spot={fields.trackingSpotName?.value}
      style={spotBackgroundStyle}
      className={classNames('container-fluid', spotThemeClass)}
    >
      <div className="container !py-0">
        <div className="mb-12">
          {headline?.value && <h2 className="heading-2 mb-6">{headline.value}</h2>}
          {showSearchBox?.value && (
            <div className="w-full md:w-1/2">
              <div className="relative">
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-full border border-gray-400 px-4 py-2 pl-10 text-black"
                  placeholder={t('system.Search')}
                  aria-label={t('system.Search')}
                />

                <JabraIcon
                  Icon={icon_search}
                  className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
                />
              </div>
            </div>
          )}
        </div>

        {hasNoResults && (
          <p className="my-24 text-center text-gray-500">{t('system.DownloadsNoResults')}</p>
        )}
        {filteredGroups?.map((group) => (
          <DownloadableResourceGroup key={group.id} group={group} />
        ))}
      </div>
    </div>
  );
};

export default withDatasourceCheck()<DownloadableResourceListProps>(DownloadableResourceList);
